import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatRow = ({ label, value }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-gray-700/50 last:border-0">
    <span className="text-gray-400 text-xs">{label}</span>
    <span className="text-white font-semibold text-sm">{value}</span>
  </div>
);

export const TeamStatsModal = ({ teamName, sport, espnUrl, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const resp = await axios.get(`${API}/teams/stats`, {
          params: { team: teamName, sport }
        });
        setStats(resp.data);
      } catch {
        setStats({ team: teamName, sport: sport?.toUpperCase(), error: 'Failed to load stats' });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [teamName, sport]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-sm shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="team-stats-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
          <div>
            <h3 className="text-white font-bold text-base">{teamName}</h3>
            <span className="text-gray-400 text-xs">{stats?.sport || sport?.toUpperCase()}</span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg"
            data-testid="team-stats-modal-close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block w-6 h-6 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mb-2" />
              <p className="text-gray-400 text-sm">Loading stats...</p>
            </div>
          ) : stats?.error ? (
            <div className="text-center py-6">
              <p className="text-gray-400 text-sm">{stats.error}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Record - Big */}
              <div className="text-center py-3 mb-2 bg-gray-800/50 rounded-lg">
                <div className="text-3xl font-bold text-white">{stats.record}</div>
                {stats.pct && <div className="text-gray-400 text-xs mt-1">{stats.pct} Win%</div>}
                {stats.points !== undefined && (
                  <div className="text-sky-400 text-sm font-semibold mt-1">{stats.points} pts</div>
                )}
              </div>

              {/* Stats Grid */}
              {stats.division && <StatRow label="Division" value={stats.division} />}
              {stats.conference && <StatRow label="Conference" value={stats.conference} />}
              {stats.division_rank && <StatRow label="Division Rank" value={stats.division_rank} />}
              {stats.conference_rank && <StatRow label="Conference Rank" value={stats.conference_rank} />}
              {stats.league_rank && <StatRow label="League Rank" value={stats.league_rank} />}
              {stats.games_back && stats.games_back !== '—' && <StatRow label="Games Back" value={stats.games_back} />}
              {stats.streak && <StatRow label="Streak" value={stats.streak} />}
              {stats.home_record && stats.home_record !== '-' && <StatRow label="Home" value={stats.home_record} />}
              {stats.away_record && stats.away_record !== '-' && <StatRow label="Away" value={stats.away_record} />}
              {stats.last_10 && stats.last_10 !== '-' && <StatRow label="Last 10" value={stats.last_10} />}
              {stats.goals_for !== undefined && (
                <StatRow label="Goals" value={`${stats.goals_for} GF / ${stats.goals_against} GA (${stats.goal_diff > 0 ? '+' : ''}${stats.goal_diff})`} />
              )}
              {stats.diff && stats.diff !== '-' && <StatRow label="Point Diff" value={stats.diff} />}

              {/* Next Game */}
              {stats.next_game && (
                <div className="mt-3 p-2.5 bg-sky-900/30 rounded-lg border border-sky-700/30">
                  <div className="text-sky-400 text-[10px] font-semibold uppercase mb-1">Next Game</div>
                  <div className="text-white text-xs font-medium">
                    {stats.next_game.away} @ {stats.next_game.home}
                  </div>
                  <div className="text-gray-400 text-[10px]">{stats.next_game.date}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - ESPN Link */}
        {espnUrl && (
          <div className="px-4 pb-4">
            <a
              href={espnUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="team-stats-espn-link"
            >
              View Full Stats on ESPN
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
